import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import {
  useCloseOutChecklistData,
  useProcessSaveWirResponse,
  useProcessSection,
  useSectionEquipmentIdentificationData,
  useSectionInitialRequestInformationData,
  useSectionManagerialSummaryData,
  useSectionOrganiseKickOffMeetingData,
  useSectionWellInformationData,
} from 'pages/WellGuardian/WIR/hooks';
import {
  getIsUnplannedReactiveScope,
  getKomTechnicalWirReactiveScopeData,
} from 'pages/WellGuardian/WIR/utils';
import {
  Phase,
  WIREngineeringWorkSections,
  WIRKofMeetingTechnicalWirSections,
  WIRReactiveScopeSections,
  WIRStageGate2Sections,
  WIRStageGate3Sections,
} from 'pages/WellGuardian/WIR/wir.constants';
import { useCallback, useState } from 'react';
import { IntlShape } from 'react-intl';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  updateOriginalSelectedWIR,
  updateSelectedSection,
  updateSelectedWIR,
  updateWIRDraftComment,
} from '../../../../redux/WellGuardian';
import {
  useCountryState,
  useOriginalSelectedWIR,
  useSelectedWIR,
} from '../../../../redux/WellGuardian/selectors';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import { CountryData, WIR, WIRComment } from 'types/wellGuardian';

import { useSectionAssignInterventionSpdtData } from './useSectionAssignInterventionSpdtData';
import { useSectionBudgetData } from './useSectionBudgetData';
import { useSectionEngineeringAnalysisData } from './useSectionEngineeringAnalysisData';
import { useSectionIWOPData } from './useSectionIWOPData';
import {
  getEngineeringWorkPhaseName,
  getEngineeringWorkSectionKey,
  getKickOffPhaseName,
} from '../utils/getSectionData';
import { useWellHandOverChecklistData } from './useWellHandOverChecklistData';

export const useSectionUpdate = (section: string, intl: IntlShape, sectionChangeTo?: string) => {
  const [wirUpdateLoading, setWirUpdateLoading] = useState<boolean>(false);
  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState<boolean>(false);

  const wirFromState = useSelectedWIR();
  const originalWir = useOriginalSelectedWIR();
  const dispatch = useDispatch();
  const { saveInitialRequestInformation } = useSectionInitialRequestInformationData();
  const { saveOrganiseKickOffMeetingSection } = useSectionOrganiseKickOffMeetingData();
  const { saveBudget } = useSectionBudgetData();
  const { saveWellInformationSection } = useSectionWellInformationData();
  const { saveManagerialSummarySection } = useSectionManagerialSummaryData();
  const { saveAssignInterventionSpdtSection } = useSectionAssignInterventionSpdtData();
  const { saveAssignEngineeringAnalysisSection } = useSectionEngineeringAnalysisData();
  const { saveAssignIWOPSection } = useSectionIWOPData();
  const processSaveWirResponse = useProcessSaveWirResponse();
  const { saveEquipmentIdentificationSection } = useSectionEquipmentIdentificationData();
  const { saveCloseoutChecklistSection } = useCloseOutChecklistData();
  const { saveWellHandOverChecklistSection } = useWellHandOverChecklistData();
  const { processSection } = useProcessSection(section);

  const countryState = useCountryState();

  const cancelSaveChanges = useCallback(() => {
    dispatch(updateSelectedWIR(originalWir));
    /* istanbul ignore next */
    sectionChangeTo && dispatch(updateSelectedSection(sectionChangeTo));
    setUnsavedChangesModalOpen(false);
  }, [dispatch, originalWir, sectionChangeTo]);

  const processSaveResponse = useCallback(
    (wirResponse: WIR | null) => {
      if (wirResponse?.wirNumber) {
        dispatch(updateSelectedWIR(wirResponse));
        dispatch(updateOriginalSelectedWIR(wirResponse));
        dispatch(updateWIRDraftComment('')); // Reset any draft comments
        toast.success(
          intl.formatMessage({
            id: 'wellGuardian.wir.wirCreation.wirUpdated',
          }),
          {
            hideProgressBar: false,
            theme: 'colored',
          },
        );
        /* istanbul ignore next */
        if (sectionChangeTo) {
          dispatch(updateSelectedSection(sectionChangeTo));
          setUnsavedChangesModalOpen(false);
        } else {
          processSection(wirResponse);
        }
      } else {
        toast.error(
          intl.formatMessage({
            id: 'wellGuardian.wir.wirCreation.wirUpdateError',
          }),
          {
            hideProgressBar: false,
            theme: 'colored',
          },
        );
      }
    },
    [dispatch, sectionChangeTo, processSection, intl],
  );

  const saveChecklistAndActions = useCallback(
    async (
      section: string,
      phase: string,
      assignedTo: string,
      sectionCompleteConfirmation: boolean | undefined,
      comments: WIRComment[],
    ) => {
      const allChecklistsOriginal = [
        ...(originalWir?.checklist?.generic ?? []),
        ...(originalWir?.checklist?.perforations ?? []),
        ...(originalWir?.checklist?.production_and_reservoir ?? []),
        ...(originalWir?.checklist?.pumping_operation ?? []),
        ...(originalWir?.checklist?.well_stimulation ?? []),
        ...(originalWir?.checklist?.ct_operation ?? []),
      ];
      const allChecklistsState = [
        /* istanbul ignore next */
        ...(wirFromState?.checklist?.generic ?? []),
        /* istanbul ignore next */
        ...(wirFromState?.checklist?.perforations ?? []),
        /* istanbul ignore next */
        ...(wirFromState?.checklist?.production_and_reservoir ?? []),
        /* istanbul ignore next */
        ...(wirFromState?.checklist?.pumping_operation ?? []),
        /* istanbul ignore next */
        ...(wirFromState?.checklist?.well_stimulation ?? []),
        /* istanbul ignore next */
        ...(wirFromState?.checklist?.ct_operation ?? []),
      ];
      const newChecks = differenceWith(allChecklistsState, allChecklistsOriginal, isEqual);
      const allActionsOriginal = originalWir?.actions ?? [];
      /* istanbul ignore next */
      const allActionsState = wirFromState?.actions ?? [];
      const newActions = differenceWith(allActionsState, allActionsOriginal, isEqual);

      wirFromState?.wirNumber &&
        processSaveResponse(
          await wellGuardianApiClient.updateWIRChecksActions(wirFromState?.wirNumber, {
            phase,
            section,
            assignedTo,
            comments,
            sectionCompleteConfirmation,
            actions: newActions,
            checks: newChecks,
          }),
        );
    },
    [wirFromState, originalWir, processSaveResponse],
  );

  const getKomReviewCheckListActions = useCallback(async () => {
    const { phase: phaseKomOrRs } = getKomTechnicalWirReactiveScopeData(wirFromState, countryState);
    if (countryState === CountryData.denmark) {
      (section === WIRStageGate2Sections.reviewChecklistActions ||
        section === WIRReactiveScopeSections.reviewChecklistActions) &&
        (await saveChecklistAndActions(
          'REVIEW_CHECKLIST_ACTIONS',
          /* istanbul ignore next */
          getIsUnplannedReactiveScope(wirFromState) ? 'REACTIVE_SCOPE' : 'STAGE_GATE_2',
          /* istanbul ignore next */
          wirFromState?.phases?.[phaseKomOrRs]?.sections?.reviewChecklistActions?.assignedTo || '',
          wirFromState?.phases?.[phaseKomOrRs]?.sections?.reviewChecklistActions
            ?.sectionCompleteConfirmation,
          /* istanbul ignore next */
          wirFromState?.phases?.[phaseKomOrRs]?.sections?.reviewChecklistActions?.comments || [],
        ));
    } else {
      (section === WIRKofMeetingTechnicalWirSections.reviewChecklistActions ||
        section === WIRReactiveScopeSections.reviewChecklistActions) &&
        /* istanbul ignore next */
        (await saveChecklistAndActions(
          'REVIEW_CHECKLIST_ACTIONS',
          getIsUnplannedReactiveScope(wirFromState)
            /* istanbul ignore next */
            ? 'REACTIVE_SCOPE'
            /* istanbul ignore next */
            : getKickOffPhaseName(countryState),
          /* istanbul ignore next */
          wirFromState?.phases?.[phaseKomOrRs]?.sections?.reviewChecklistActions?.assignedTo || '',
          wirFromState?.phases?.[phaseKomOrRs]?.sections?.reviewChecklistActions
            ?.sectionCompleteConfirmation,
          /* istanbul ignore next */
          wirFromState?.phases?.[phaseKomOrRs]?.sections?.reviewChecklistActions?.comments || [],
        ));
    }
  }, [countryState, saveChecklistAndActions, section, wirFromState]);

  const getEnggWorkReviewCheckListActions = useCallback(async () => {
    // engineeringWorkSectionKey
    const engineeringWorkSectionKey = getEngineeringWorkSectionKey(countryState);
    const engineeringWorkPhase = getEngineeringWorkPhaseName(countryState);
    (section === WIRStageGate3Sections.reviewChecklistActions ||
      section === WIREngineeringWorkSections.reviewChecklistActions) &&
      /* istanbul ignore next */
      (await saveChecklistAndActions(
        'REVIEW_CHECKLIST_ACTIONS',
        engineeringWorkPhase,
        /* istanbul ignore next */
        wirFromState?.phases?.[engineeringWorkSectionKey]?.sections?.reviewChecklistActions
          /* istanbul ignore next */
          ?.assignedTo || '',
        wirFromState?.phases?.[engineeringWorkSectionKey]?.sections?.reviewChecklistActions
          ?.sectionCompleteConfirmation,
        /* istanbul ignore next */
        wirFromState?.phases?.[engineeringWorkSectionKey]?.sections?.reviewChecklistActions
          /* istanbul ignore next */
          ?.comments || [],
      ));
  }, [countryState, saveChecklistAndActions, section, wirFromState]);

  const saveWir = useCallback(
    async (updatedPhase: Phase) => {
      if (wirFromState?.wirNumber) {
        setWirUpdateLoading(true);

        // use for common api - updateWIRGenericSection
        await processSaveWirResponse(section, processSaveResponse, updatedPhase);

        await saveInitialRequestInformation(section, processSaveResponse);
        await saveOrganiseKickOffMeetingSection(section, processSaveResponse);
        await saveEquipmentIdentificationSection(section, processSaveResponse);
        await saveBudget(section, processSaveResponse);
        await saveWellInformationSection(section, processSaveResponse);
        await saveManagerialSummarySection(section, processSaveResponse);
        await saveAssignInterventionSpdtSection(section, processSaveResponse);
        await saveAssignEngineeringAnalysisSection(section, processSaveResponse);
        await saveCloseoutChecklistSection(section, processSaveResponse);
        await saveWellHandOverChecklistSection(section, processSaveResponse);
        await saveAssignIWOPSection(section, processSaveResponse);

        getKomReviewCheckListActions();
        getEnggWorkReviewCheckListActions();

        setWirUpdateLoading(false);
      }
    },
    [
      wirFromState?.wirNumber,
      processSaveWirResponse,
      section,
      processSaveResponse,
      saveInitialRequestInformation,
      saveOrganiseKickOffMeetingSection,
      saveEquipmentIdentificationSection,
      saveBudget,
      saveWellInformationSection,
      saveManagerialSummarySection,
      saveAssignInterventionSpdtSection,
      saveAssignEngineeringAnalysisSection,
      saveAssignIWOPSection,
      saveCloseoutChecklistSection,
      saveWellHandOverChecklistSection,
      getKomReviewCheckListActions,
      getEnggWorkReviewCheckListActions,
    ],
  );

  return {
    saveWir,
    wirUpdateLoading,
    setUnsavedChangesModalOpen,
    unsavedChangesModalOpen,
    cancelSaveChanges,
    processSaveResponse,
  };
};
